import React, { useState, useEffect } from 'react';
import {
  Archive,
  CircleCheck,
  CircleX,
  Download,
  Eye,
  MoreHorizontal,
  Plus,
  RefreshCw,
  RotateCcw,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { backupService, BackupInfo, RestoreOptions, RestoreResult, BackupPreview } from '../../services/backupService';
import { BackupPreviewModal } from './BackupPreviewModal';
import { ConfirmModal } from '../common/ConfirmModal';
import {
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  EmptyState,
  IconButton,
  Menu,
  Pill,
  SectionHeader,
  Skeleton,
  Spinner,
  Switch,
} from '../ui';

const shortDate = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export const BackupManagement: React.FC = () => {
  const [backups, setBackups] = useState<BackupInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedBackup, setSelectedBackup] = useState<string | null>(null);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [preview, setPreview] = useState<BackupPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [restoreOptions, setRestoreOptions] = useState<RestoreOptions>({});
  const [restoreResult, setRestoreResult] = useState<RestoreResult | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [showRestoreDetails, setShowRestoreDetails] = useState(false);

  useEffect(() => {
    loadBackups();
  }, []);

  const loadBackups = async () => {
    setLoading(true);
    setError(null);
    setNotice(null);
    try {
      const result = await backupService.listBackups();
      if (result.success) {
        setBackups(result.backups);
      } else {
        setError('Failed to load backups');
      }
    } catch (err) {
      setError('Failed to load backups');
      console.error('Error loading backups:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBackup = async () => {
    setLoading(true);
    setCreating(true);
    setError(null);
    try {
      const result = await backupService.createBackup({ includeMedia: true });
      await loadBackups();
      // After the refresh: loadBackups clears the message area
      if (!result.success) {
        setError(result.error ?? "The backup couldn't be created.");
      } else {
        const missing = result.metadata?.media.missing.length ?? 0;
        setNotice(
          missing > 0
            ? `Backup saved, but ${missing} file${missing === 1 ? '' : 's'} could not be read from storage and ${missing === 1 ? 'is' : 'are'} not in it.`
            : 'Backup saved.',
        );
      }
    } catch (err) {
      setError("The backup couldn't be created.");
      console.error('Error creating backup:', err);
    } finally {
      setLoading(false);
      setCreating(false);
    }
  };

  const handleDownloadBackup = async (backupName: string) => {
    try {
      const blob = await backupService.downloadBackup(backupName);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = backupName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to download backup');
      console.error('Error downloading backup:', err);
    }
  };

  // Confirmed through the ConfirmModal below (was window.confirm)
  const handleDeleteBackup = async (backupName: string) => {
    try {
      const result = await backupService.deleteBackup(backupName);
      if (result.success) {
        await loadBackups();
      } else {
        setError(result.error || 'Failed to delete backup');
      }
    } catch (err) {
      setError('Failed to delete backup');
      console.error('Error deleting backup:', err);
    }
  };

  const handlePreviewBackup = async (backupName: string) => {
    setSelectedBackup(backupName);
    setPreviewLoading(true);
    setError(null);
    try {
      const result = await backupService.previewBackup(backupName);
      if (result.success && result.preview) {
        setPreview(result.preview);
        setShowPreviewModal(true);
      } else {
        setError(result.error || 'Failed to load backup preview');
      }
    } catch (err) {
      setError('Failed to load backup preview');
      console.error('Error loading backup preview:', err);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleConfirmRestore = () => {
    setShowPreviewModal(false);
    setShowRestoreModal(true);
  };

  const handleRestoreBackup = async () => {
    if (!selectedBackup) return;

    setLoading(true);
    setError(null);
    try {
      const result = await backupService.restoreBackup(selectedBackup, restoreOptions);
      setRestoreResult(result);
      setShowRestoreModal(false);
      
      if (result.success) {
        await loadBackups();
      }
    } catch (err) {
      setError('Failed to restore backup');
      console.error('Error restoring backup:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError(null);
    try {
      const result = await backupService.uploadBackup(file);
      if (result.success) {
        await loadBackups();
      } else {
        setError(result.error || 'Failed to upload backup');
      }
    } catch (err) {
      setError('Failed to upload backup');
      console.error('Error uploading backup:', err);
    } finally {
      setUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const latestBackup = backups.reduce<BackupInfo | null>(
    (latest, backup) =>
      !latest || new Date(backup.createdAt) > new Date(latest.createdAt) ? backup : latest,
    null,
  );

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await handleDeleteBackup(deleteTarget);
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const restoreIssues = restoreResult ? restoreResult.errors.length + restoreResult.warnings.length : 0;
  const storage = backups[0]?.storage;

  return (
    <section>
      <SectionHeader
        title="Backups"
        aside={
          <>
            <IconButton label="Refresh" size="sm" onClick={loadBackups} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading && !creating ? 'animate-spin' : ''}`} strokeWidth={1.75} />
            </IconButton>
            <label
              title="Upload a backup (.zip)"
              className={`inline-flex h-8 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full bg-white px-2.5 text-[13px] font-medium text-ink ring-1 ring-inset ring-neutral-200 transition-colors focus-within:ring-2 focus-within:ring-primary-600/40 hover:bg-neutral-50 sm:px-3 ${
                uploading ? 'pointer-events-none text-neutral-400' : ''
              }`}
            >
              {uploading ? (
                <Spinner />
              ) : (
                <Upload className="h-4 w-4 text-neutral-400" strokeWidth={1.75} />
              )}
              <span className="sr-only sm:not-sr-only">{uploading ? 'Uploading' : 'Upload'}</span>
              <input type="file" accept=".zip" onChange={handleFileUpload} className="sr-only" disabled={uploading} />
            </label>
            <Button size="sm" variant="primary" onClick={handleCreateBackup} disabled={loading} loading={creating}>
              {!creating && <Plus className="h-4 w-4" strokeWidth={2} />}
              Create backup
            </Button>
          </>
        }
      />

      {error && (
        <p role="alert" className="mb-3 animate-fade-up rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">
          {error}
        </p>
      )}

      {notice && !error && (
        <p role="status" className="mb-3 animate-fade-up rounded-xl bg-neutral-50 px-3.5 py-2.5 text-[13px] text-neutral-600">
          {notice}
        </p>
      )}

      {storage && (
        <p className="mb-2 text-[12px] text-neutral-400">
          Kept in {storage.type === 's3' ? `the file store at ${storage.location}` : `local storage (${storage.location})`}
        </p>
      )}

      {/* Not the kit Card: its overflow-hidden would clip the row menus */}
      <div className="rounded-2xl border border-neutral-200 bg-white">
        {restoreResult && (
          <div role="status" className="animate-fade-up border-b border-neutral-200/70 px-4 py-2.5">
            <div className="flex items-center gap-2 text-[13px]">
              {restoreResult.success ? (
                <CircleCheck className="h-4 w-4 flex-shrink-0 text-emerald-600" strokeWidth={1.75} />
              ) : (
                <CircleX className="h-4 w-4 flex-shrink-0 text-primary-600" strokeWidth={1.75} />
              )}
              <span className="min-w-0 flex-1 truncate">
                {restoreResult.success ? 'Restored' : 'Restore failed'}
                <span className="text-neutral-500">
                  {' · '}
                  <span className="tabular-nums">{restoreResult.restoredRecords.toLocaleString()}</span> records
                </span>
                {restoreResult.errors.length > 0 && (
                  <span className="tabular-nums text-primary-700"> · {restoreResult.errors.length} errors</span>
                )}
                {restoreResult.warnings.length > 0 && (
                  <span className="tabular-nums text-amber-700"> · {restoreResult.warnings.length} warnings</span>
                )}
              </span>
              {restoreIssues > 0 && (
                <Button size="xs" variant="ghost" onClick={() => setShowRestoreDetails((v) => !v)} aria-expanded={showRestoreDetails}>
                  Details
                </Button>
              )}
              <IconButton label="Dismiss" size="xs" onClick={() => setRestoreResult(null)}>
                <X className="h-3.5 w-3.5" strokeWidth={1.75} />
              </IconButton>
            </div>
            {showRestoreDetails && restoreIssues > 0 && (
              <ul className="mt-2 max-h-48 space-y-1 overflow-y-auto pl-6 text-[12px]">
                {restoreResult.errors.map((message, index) => (
                  <li key={`e-${index}`} className="break-words text-primary-700">
                    {message}
                  </li>
                ))}
                {restoreResult.warnings.map((message, index) => (
                  <li key={`w-${index}`} className="break-words text-amber-700">
                    {message}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {loading && backups.length === 0 ? (
          <div className="divide-y divide-neutral-200/70" aria-busy="true" aria-label="Loading backups">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3">
                <Skeleton className="h-8 w-8 rounded-full" />
                <Skeleton className="h-4 w-48" />
              </div>
            ))}
          </div>
        ) : backups.length === 0 ? (
          <EmptyState icon={Archive} title="No backups" />
        ) : (
          <>
            <div className="hidden h-9 items-center gap-3 border-b border-neutral-200/70 px-4 text-[12px] text-neutral-400 sm:flex">
              <span className="w-8 flex-shrink-0" />
              <span className="flex-1">Name</span>
              <span className="w-20 text-right">Size</span>
              <span className="w-20 text-right">Records</span>
              <span className="hidden w-44 text-right md:block">Created</span>
              <span className="w-[92px]" />
            </div>
            <ul className="divide-y divide-neutral-200/70">
              {backups.map((backup) => {
                const busy = previewLoading && selectedBackup === backup.name;
                const size = backupService.formatFileSize(backup.size);
                const records = backup.records === null ? '—' : backup.records.toLocaleString();
                const files =
                  backup.includesMedia && backup.mediaFiles !== null
                    ? `${backup.mediaFiles.toLocaleString()} files`
                    : backup.includesMedia === false
                      ? 'no files'
                      : '';
                return (
                  <li
                    key={backup.name}
                    className="flex items-center gap-3 px-4 py-2.5 transition-colors last:rounded-b-2xl hover:bg-neutral-50"
                  >
                    <span className="hidden h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-neutral-100 text-neutral-500 sm:inline-flex">
                      <Archive className="h-4 w-4" strokeWidth={1.75} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[14px] text-ink" title={backup.name}>
                        {backup.name}
                      </p>
                      <p className="truncate text-[12px] tabular-nums text-neutral-400 md:hidden">
                        <span className="sm:hidden">
                          {size} · {records} records{files ? ` · ${files}` : ''} ·{' '}
                        </span>
                        <span title={backupService.formatDate(backup.createdAt)}>{shortDate(backup.createdAt)}</span>
                      </p>
                    </div>
                    <span className="hidden w-20 text-right text-[13px] tabular-nums text-neutral-500 sm:block">{size}</span>
                    <span className="hidden w-20 text-right text-[13px] tabular-nums text-neutral-500 sm:block">
                      {records}
                      {files && (
                        <span className="block text-[11px] text-neutral-400">{files}</span>
                      )}
                    </span>
                    <span
                      className="hidden w-44 text-right text-[13px] tabular-nums text-neutral-500 md:block"
                      title={backupService.formatDate(backup.createdAt)}
                    >
                      {shortDate(backup.createdAt)}
                    </span>
                    <div className="-mr-2 flex flex-shrink-0 items-center justify-end gap-1 sm:mr-0 sm:w-[92px]">
                      {busy ? (
                        <Spinner className="h-3.5 w-3.5 text-neutral-400" />
                      ) : latestBackup?.name === backup.name ? (
                        <Pill tone="green">Latest</Pill>
                      ) : null}
                      <Menu
                        trigger={(props) => (
                          <IconButton label={`Actions for ${backup.name}`} size="sm" {...props}>
                            <MoreHorizontal className="h-4 w-4" strokeWidth={1.75} />
                          </IconButton>
                        )}
                        items={[
                          { label: 'Preview', icon: Eye, onSelect: () => handlePreviewBackup(backup.name), disabled: previewLoading },
                          { label: 'Restore…', icon: RotateCcw, onSelect: () => handlePreviewBackup(backup.name), disabled: previewLoading },
                          { label: 'Download', icon: Download, onSelect: () => handleDownloadBackup(backup.name) },
                          { label: 'Delete', icon: Trash2, danger: true, onSelect: () => setDeleteTarget(backup.name) },
                        ]}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>

      {/* Preview Modal */}
      <BackupPreviewModal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        onConfirm={handleConfirmRestore}
        preview={preview}
        loading={previewLoading}
      />

      {/* Restore Modal */}
      <Dialog
        open={showRestoreModal}
        onClose={() => setShowRestoreModal(false)}
        labelledBy="restore-backup-title"
        handle
        className="sm:max-w-md"
      >
        <DialogHeader
          id="restore-backup-title"
          eyebrow="Restore backup"
          title={<span className="break-all">{selectedBackup}</span>}
          onClose={() => setShowRestoreModal(false)}
        />
        <DialogBody>
          <p className="mb-3 text-[13px] text-neutral-600">
            Restoring writes this backup's records into the live system. Passwords and AI keys
            already set here are kept — a backup never stores them.
          </p>
          <div className="divide-y divide-neutral-200/70 overflow-hidden rounded-2xl border border-neutral-200">
            <div className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <p className="text-[13px] text-ink">Clear existing data</p>
                <p className={`text-[12px] ${restoreOptions.clearExisting ? 'text-primary-700' : 'text-neutral-400'}`}>
                  Deletes current data before restoring
                </p>
              </div>
              <Switch
                label="Clear existing data before restore"
                checked={restoreOptions.clearExisting || false}
                onChange={(checked) => setRestoreOptions({ ...restoreOptions, clearExisting: checked })}
              />
            </div>
            <div className="flex items-center justify-between gap-3 px-4 py-3">
              <p className="text-[13px] text-ink">Skip users</p>
              <Switch
                label="Skip user data"
                checked={restoreOptions.skipUsers || false}
                onChange={(checked) => setRestoreOptions({ ...restoreOptions, skipUsers: checked })}
              />
            </div>
            <div className="flex items-center justify-between gap-3 px-4 py-3">
              <p className="text-[13px] text-ink">Skip audit logs</p>
              <Switch
                label="Skip audit logs"
                checked={restoreOptions.skipAuditLogs || false}
                onChange={(checked) => setRestoreOptions({ ...restoreOptions, skipAuditLogs: checked })}
              />
            </div>
            <div className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <p className="text-[13px] text-ink">Put files back</p>
                <p className="text-[12px] text-neutral-400">
                  Copies photos and documents from the backup into storage
                </p>
              </div>
              <Switch
                label="Restore media files"
                checked={restoreOptions.restoreMedia !== false}
                onChange={(checked) => setRestoreOptions({ ...restoreOptions, restoreMedia: checked })}
              />
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => setShowRestoreModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleRestoreBackup} loading={loading}>
            Restore
          </Button>
        </DialogFooter>
      </Dialog>

      <ConfirmModal
        open={!!deleteTarget}
        title="Delete backup"
        message={
          <span>
            Delete <span className="break-all text-ink">{deleteTarget}</span>? This can’t be undone.
          </span>
        }
        confirmText="Delete"
        loading={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </section>
  );
};
